'use client';

import { useState, type SyntheticEvent } from 'react';

import { useRouter } from 'next/navigation';

import { MARKETING_ROUTES } from '@/common/constants';
import { ScanEmailModal } from '@/components/modals/scan-email-modal';
import { useScanDomain } from '@/components/scan-domain-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SCANNER_COPY } from '@/lib/content';
import { stashScanEmail } from '@/lib/scan-handoff';
import { cn } from '@/lib/utils';
import { URL_REGEX } from '@/lib/validation';

/**
 * Only the field fits here - the scan itself (radar, findings, report) runs on /scan. Taking the
 * email on the way out is what lets /scan start scanning on arrival instead of stopping the
 * visitor for a second form.
 */
export function ScanStartForm({ className }: { className?: string }) {
  const router = useRouter();
  const { domain, setDomain } = useScanDomain();
  const [error, setError] = useState(false);
  const [askingEmail, setAskingEmail] = useState(false);

  function submit(event: SyntheticEvent) {
    event.preventDefault();
    if (!URL_REGEX.test(domain.trim())) {
      setError(true);
      return;
    }
    setAskingEmail(true);
  }

  function start(email: string) {
    setAskingEmail(false);
    // The email rides in session storage rather than the query string: /scan is a shareable,
    // linkable URL and an address in it would leak into history, referrers, and server logs.
    stashScanEmail(email);
    router.push(`${MARKETING_ROUTES.scan}?domain=${encodeURIComponent(domain.trim())}`);
  }

  return (
    <>
      <form onSubmit={submit} noValidate className={cn('max-w-[480px]', className)}>
        <div className="flex flex-col gap-2.5 sm:flex-row">
          <Input
            value={domain}
            onChange={event => {
              setDomain(event.target.value);
              setError(false);
            }}
            placeholder={SCANNER_COPY.placeholder}
            aria-label="Your website address"
            aria-invalid={error}
            className="h-12 min-h-12 flex-1 px-4 font-mono"
          />
          <Button type="submit" className="h-12 px-5 text-base font-semibold">
            {SCANNER_COPY.button}
          </Button>
        </div>
        {error && <p className="mt-2 text-sm text-brand">{SCANNER_COPY.validationError}</p>}
      </form>
      <ScanEmailModal
        open={askingEmail}
        domain={domain.trim()}
        onClose={() => setAskingEmail(false)}
        onConfirm={start}
      />
    </>
  );
}
